// @ts-nocheck
import { BlobServiceClient } from "@azure/storage-blob";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { FaEraser } from "react-icons/fa";
import SignatureCanvas from "react-signature-canvas";
import { blobStorageApi } from "src/api/blob-storage.api";
import {
    engaugeAssistantApi,
    ValidatedEmployee,
} from "src/api/engauge-assistant.api";
import { psTabletManagementApi } from "src/api/platform-science-tablet-management.api";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";

type CheckoutFormData = {
    terminal: string;
    isTabletDamaged: boolean;
    isStylusMissing: boolean;
    issuedBy: string;
    tabletSerialNumber: string;
    driverCode: string;
    unitNumber: string;
    signatureImageBase64: string;
};

type CheckInFormData = {
    terminal: string;
    isTabletDamaged: boolean;
    isStylusMissing: boolean;
    receivedBy: string;
    tabletSerialNumber: string;
    driverCode: string;
    signatureImageBase64: string;
};

function TabletInventory() {
    const [validatedDriver, setValidatedDriver] =
        useState<ValidatedEmployee | null>(null);
    const [isCheckOut, setIsCheckOut] = useState<boolean>(true);
    const checkoutForm = useForm<CheckoutFormData>();
    const checkInForm = useForm<CheckInFormData>();

    async function onCheckoutFormData(checkoutFormData: CheckoutFormData) {
        console.log(checkoutFormData);

        try {
            // Validate employee code
            const employee = await engaugeAssistantApi.getEmployee({
                params: { employeeCode: checkoutFormData.driverCode },
            });

            // Upload signature image
            const blobName = `signatures/${uuidv4()}.png`;
            const auth = await blobStorageApi.auth();

            const blobServiceClient = new BlobServiceClient(
                `https://${auth.account}.blob.core.windows.net?${auth.sas}`,
            );
            const containerClient = blobServiceClient.getContainerClient(
                auth.container,
            );
            const blockBlobClient =
                containerClient.getBlockBlobClient(blobName);
            const blob = await (
                await fetch(checkoutFormData.signatureImageBase64)
            ).blob();
            await blockBlobClient.uploadData(blob, {
                blobHTTPHeaders: { blobContentType: "image/png" },
            });

            // Check out tablet
            const checkOutTabletResult =
                await psTabletManagementApi.checkOutTablet({
                    body: {
                        driverCode: checkoutFormData.driverCode,
                        driverSignatureUrl: blobName,
                        emailReceipt: import.meta.env.DEV
                            ? "noel.garces@m-v-t.com"
                            : employee.EmailPersonal,
                        employeeCode: checkoutFormData.issuedBy,
                        stylusMissing: checkoutFormData.isStylusMissing,
                        tabletDamaged: checkoutFormData.isTabletDamaged,
                        terminal: "El Paso",
                        unitCode: checkoutFormData.unitNumber,
                    },
                    params: { tabletId: checkoutFormData.tabletSerialNumber },
                });

            // Override process
            if (checkOutTabletResult.status === "outstanding_assignment") {
                const overrideSwal = await Swal.fire({
                    input: "textarea",
                    inputLabel:
                        "Reason for allowing multiple tablet assignments",
                    inputPlaceholder: "Type your reason here...",
                    showCancelButton: false,
                    confirmButtonText: "Submit",
                    confirmButtonColor: "#66cc8a",
                    inputValidator: (value) => {
                        if (!value) {
                            return "Reason is required";
                        }
                    },
                });

                if (!overrideSwal.isConfirmed) return;

                const confirmationSwal = await Swal.fire({
                    title: "Are you sure?",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes",
                    cancelButtonText: "No",
                    confirmButtonColor: "#66cc8a",
                    cancelButtonColor: "#ff5861",
                });

                if (!confirmationSwal.isConfirmed) return;

                // Checkout tablet with override
                const checkOutTabletOverrideResult =
                    await psTabletManagementApi.checkOutTablet({
                        body: {
                            driverCode: checkoutFormData.driverCode,
                            driverSignatureUrl: blobName,
                            emailReceipt: import.meta.env.DEV
                                ? "noel.garces@m-v-t.com"
                                : employee.EmailPersonal,
                            employeeCode: checkoutFormData.issuedBy,
                            stylusMissing: checkoutFormData.isStylusMissing,
                            tabletDamaged: checkoutFormData.isTabletDamaged,
                            terminal: checkoutFormData.terminal,
                            unitCode: checkoutFormData.unitNumber,
                            overrideReason: overrideSwal.value,
                        },
                        params: {
                            tabletId: checkoutFormData.tabletSerialNumber,
                        },
                    });

                if (checkOutTabletOverrideResult.status !== "ok") {
                    toast.error("Failed to check out tablet with override.");
                    return;
                }

                toast.success("Tablet checked out successfully with override.");
            }

            // Reset form
            checkoutForm.reset({
                terminal: "",
                isTabletDamaged: false,
                isStylusMissing: false,
                issuedBy: "",
                tabletSerialNumber: "",
                driverCode: "",
                unitNumber: "",
                signatureImageBase64: "",
            });
            setValidatedDriver(null);
            toast.success("Tablet checked out successfully.");
        } catch (error) {
            console.log(error);
            toast.error(error.message);
        }
    }

    async function onCheckIn(checkInFormData: CheckInFormData) {
        console.log(checkInFormData);

        try {
            // Validate employee code
            const employee = await engaugeAssistantApi.getEmployee({
                params: { employeeCode: checkInFormData.driverCode },
            });

            // Upload signature image
            const blobName = `signatures/${uuidv4()}.png`;
            const auth = await blobStorageApi.auth();

            const blobServiceClient = new BlobServiceClient(
                `https://${auth.account}.blob.core.windows.net?${auth.sas}`,
            );
            const containerClient = blobServiceClient.getContainerClient(
                auth.container,
            );
            const blockBlobClient =
                containerClient.getBlockBlobClient(blobName);
            const blob = await (
                await fetch(checkInFormData.signatureImageBase64)
            ).blob();
            await blockBlobClient.uploadData(blob, {
                blobHTTPHeaders: { blobContentType: "image/png" },
            });

            // Check in tablet
            await psTabletManagementApi.checkInTablet({
                body: {
                    driverCode: checkInFormData.driverCode,
                    driverSignatureUrl: checkInFormData.signatureImageBase64,
                    emailReceipt: employee.EmailPersonal,
                    employeeCode: checkInFormData.receivedBy,
                    stylusMissing: checkInFormData.isStylusMissing,
                    tabletDamaged: checkInFormData.isTabletDamaged,
                    terminal: checkInFormData.terminal,
                    // overrideReason: "asdf",
                },
                params: { tabletId: checkInFormData.tabletSerialNumber },
            });

            // Reset form
            checkInForm.reset({
                terminal: "",
                isTabletDamaged: false,
                isStylusMissing: false,
                receivedBy: "",
                tabletSerialNumber: "",
                driverCode: "",
                signatureImageBase64: "",
            });
            setValidatedDriver(null);
            toast.success("Tablet checked in successfully.");
        } catch (error) {
            console.log(error);
            toast.error(error.message);
        }
    }

    async function handleDriverCodeBlur() {
        const driverCode = isCheckOut
            ? checkoutForm.getValues("driverCode")
            : checkInForm.getValues("driverCode");

        if (!driverCode) return;

        try {
            const driver = await engaugeAssistantApi.getEmployee({
                params: { employeeCode: driverCode },
            });
            setValidatedDriver(driver);
        } catch (error) {
            setValidatedDriver(null);
            toast.error(error.message);
        }
    }

    useEffect(() => {
        const htmlTag = document.getElementsByTagName("html")[0];

        // set the daisyui theme to business on the html tag
        htmlTag.setAttribute("data-theme", "emerald");

        return () => {
            htmlTag.setAttribute("data-theme", "nord");
        };
    }, []);

    return (
        <div className="bg-gray-50 h-full">
            <div className="flex h-full space-x-6 p-6">
                <div className="w-1/3">
                    <div className="card bg-base-100 card-bordered card-compact">
                        <div className="card-body">
                            <fieldset className="fieldset w-full">
                                <label className="label">
                                    <span>Termial</span>
                                </label>
                                {isCheckOut && (
                                    <select
                                        className={clsx(
                                            "select select-bordered",
                                            {
                                                "border-red-500":
                                                    checkoutForm.formState
                                                        .errors.terminal,
                                            },
                                        )}
                                        {...checkoutForm.register("terminal", {
                                            required: "Terminal is required",
                                        })}
                                    >
                                        <option value="">
                                            Select terminal
                                        </option>
                                        <option value="El Paso">El Paso</option>
                                        <option value="Lared">Laredo</option>
                                        <option value="Nashville">
                                            Nashville
                                        </option>
                                    </select>
                                )}
                                {!isCheckOut && (
                                    <select
                                        className={clsx(
                                            "select select-bordered",
                                            {
                                                "border-red-500":
                                                    checkInForm.formState.errors
                                                        .terminal,
                                            },
                                        )}
                                        {...checkInForm.register("terminal", {
                                            required: "Terminal is required",
                                        })}
                                    >
                                        <option value="">
                                            Select terminal
                                        </option>
                                        <option value="El Paso">El Paso</option>
                                        <option value="Lared">Laredo</option>
                                        <option value="Nashville">
                                            Nashville
                                        </option>
                                    </select>
                                )}
                            </fieldset>
                            <div className="flex space-x-4">
                                <fieldset className="fieldset">
                                    <label className="label cursor-pointer">
                                        <span>Check Out</span>
                                        <input
                                            type="radio"
                                            name="checkinout"
                                            className="radio ml-2"
                                            checked={isCheckOut}
                                            onChange={() => setIsCheckOut(true)}
                                        />
                                    </label>
                                </fieldset>
                                <fieldset className="fieldset">
                                    <label className="label cursor-pointer">
                                        <span>Check In</span>
                                        <input
                                            type="radio"
                                            name="checkinout"
                                            className="radio ml-2"
                                            checked={!isCheckOut}
                                            onChange={() =>
                                                setIsCheckOut(false)
                                            }
                                        />
                                    </label>
                                </fieldset>
                            </div>
                        </div>
                    </div>
                    {isCheckOut && (
                        <div className="card bg-base-100 card-bordered card-compact mt-6">
                            <div className="card-body">
                                <div className="card-title">
                                    Check Out Details
                                </div>
                                <form
                                    className="space-y-2"
                                    id="checkoutDetails"
                                    onSubmit={checkoutForm.handleSubmit(
                                        onCheckoutFormData,
                                    )}
                                >
                                    {/* Checkbox Options */}
                                    <div className="flex space-x-4">
                                        <fieldset className="fieldset">
                                            <label className="label cursor-pointer">
                                                <span>Tablet Damaged</span>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox ml-2"
                                                    {...checkoutForm.register(
                                                        "isTabletDamaged",
                                                    )}
                                                />
                                            </label>
                                        </fieldset>
                                        <fieldset className="fieldset">
                                            <label className="label cursor-pointer">
                                                <span>Stylus Missing</span>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox ml-2"
                                                    {...checkoutForm.register(
                                                        "isStylusMissing",
                                                    )}
                                                />
                                            </label>
                                        </fieldset>
                                    </div>

                                    <fieldset className="fieldset w-full">
                                        <label className="label">
                                            <span>Issued By</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Type here"
                                            className={clsx(
                                                "input input-bordered w-full",
                                                {
                                                    "border-red-500":
                                                        checkoutForm.formState
                                                            .errors.issuedBy,
                                                },
                                            )}
                                            {...checkoutForm.register(
                                                "issuedBy",
                                                {
                                                    required:
                                                        "Issued by is required",
                                                },
                                            )}
                                        />
                                        {/* {checkoutForm.formState.errors?.issuedBy?.message && (
                      <p className="text-red-500 mt-1.5">{checkoutForm.formState.errors?.issuedBy?.message}</p>
                    )} */}
                                    </fieldset>
                                    <fieldset className="w-full">
                                        <label className="label">
                                            <span>Tablet Serial #</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Type here"
                                            className={clsx(
                                                "input input-bordered w-full",
                                                {
                                                    "border-red-500":
                                                        checkoutForm.formState
                                                            .errors
                                                            .tabletSerialNumber,
                                                },
                                            )}
                                            {...checkoutForm.register(
                                                "tabletSerialNumber",
                                                {
                                                    required:
                                                        "Tablet serial number is required",
                                                },
                                            )}
                                        />
                                        {/* {checkoutForm.formState.errors?.tabletSerialNumber?.message && (
                      <p className="text-red-500 mt-1.5">
                        {checkoutForm.formState.errors?.tabletSerialNumber?.message}
                      </p>
                    )} */}
                                    </fieldset>
                                    <fieldset className="fieldset w-full">
                                        <label className="label">
                                            <span>Driver Code</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Type here"
                                            className={clsx(
                                                "input input-bordered w-full",
                                                {
                                                    "border-red-500":
                                                        checkoutForm.formState
                                                            .errors.driverCode,
                                                },
                                            )}
                                            {...checkoutForm.register(
                                                "driverCode",
                                                {
                                                    required:
                                                        "Driver code is required",
                                                },
                                            )}
                                            onBlur={handleDriverCodeBlur}
                                        />
                                        {/* {checkoutForm.formState.errors?.driverCode?.message && (
                      <p className="text-red-500 mt-1.5">{checkoutForm.formState.errors?.driverCode?.message}</p>
                    )} */}
                                    </fieldset>
                                    <fieldset className="fieldset w-full">
                                        <label className="label">
                                            <span>Unit Number</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Type here"
                                            className={clsx(
                                                "input input-bordered w-full",
                                                {
                                                    "border-red-500":
                                                        checkoutForm.formState
                                                            .errors.unitNumber,
                                                },
                                            )}
                                            {...checkoutForm.register(
                                                "unitNumber",
                                                {
                                                    required:
                                                        "Unit number is required",
                                                },
                                            )}
                                        />
                                        {/* {checkoutForm.formState.errors?.unitNumber?.message && (
                      <p className="text-red-500 mt-1.5">{checkoutForm.formState.errors?.unitNumber?.message}</p>
                    )} */}
                                    </fieldset>
                                </form>
                            </div>
                        </div>
                    )}
                    {!isCheckOut && (
                        <div className="card bg-base-100 card-bordered card-compact mt-6">
                            <div className="card-body">
                                <div className="card-title">
                                    Check-In Detials
                                </div>
                                <form
                                    className="space-y-2"
                                    id="checkInDetails"
                                    onSubmit={checkInForm.handleSubmit(
                                        onCheckIn,
                                    )}
                                >
                                    {/* Checkbox Options */}
                                    <div className="flex space-x-4">
                                        <fieldset className="fieldset">
                                            <label className="label cursor-pointer">
                                                <span>Tablet Damaged</span>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox ml-2"
                                                    {...checkInForm.register(
                                                        "isTabletDamaged",
                                                    )}
                                                />
                                            </label>
                                        </fieldset>
                                        <fieldset className="fieldset">
                                            <label className="label cursor-pointer">
                                                <span>Stylus Missing</span>
                                                <input
                                                    type="checkbox"
                                                    className="checkbox ml-2"
                                                    {...checkInForm.register(
                                                        "isStylusMissing",
                                                    )}
                                                />
                                            </label>
                                        </fieldset>
                                    </div>

                                    {/* Received by */}
                                    <fieldset className="fieldset w-full">
                                        <label className="label">
                                            <span>Received By</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Type here"
                                            className={clsx(
                                                "input input-bordered w-full",
                                                {
                                                    "border-red-500":
                                                        checkInForm.formState
                                                            .errors.receivedBy,
                                                },
                                            )}
                                            {...checkInForm.register(
                                                "receivedBy",
                                                {
                                                    required:
                                                        "Received by is required",
                                                },
                                            )}
                                        />
                                        {/* {checkInForm.formState.errors?.receivedBy?.message && (
                      <p className="text-red-500 mt-1.5">{checkInForm.formState.errors?.receivedBy?.message}</p>
                    )} */}
                                    </fieldset>

                                    <fieldset className="w-full">
                                        <label className="label">
                                            <span>Tablet Serial #</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Type here"
                                            className={clsx(
                                                "input input-bordered w-full",
                                                {
                                                    "border-red-500":
                                                        checkInForm.formState
                                                            .errors
                                                            .tabletSerialNumber,
                                                },
                                            )}
                                            {...checkInForm.register(
                                                "tabletSerialNumber",
                                                {
                                                    required:
                                                        "Tablet serial number is required",
                                                },
                                            )}
                                        />
                                        {/* {checkInForm.formState.errors?.tabletSerialNumber?.message && (
                      <p className="text-red-500 mt-1.5">{checkInForm.formState.errors?.tabletSerialNumber?.message}</p>
                    )} */}
                                    </fieldset>
                                    <fieldset className="fieldset w-full">
                                        <label className="label">
                                            <span>Driver Code</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Type here"
                                            className={clsx(
                                                "input input-bordered w-full",
                                                {
                                                    "border-red-500":
                                                        checkInForm.formState
                                                            .errors.driverCode,
                                                },
                                            )}
                                            {...checkInForm.register(
                                                "driverCode",
                                                {
                                                    required:
                                                        "Driver code is required",
                                                },
                                            )}
                                            onBlur={handleDriverCodeBlur}
                                        />
                                        {/* {checkInForm.formState.errors?.driverCode?.message && (
                      <p className="text-red-500 mt-1.5">{checkInForm.formState.errors?.driverCode?.message}</p>
                    )} */}
                                    </fieldset>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
                <div className="w-2/3">
                    <div>
                        <div className="card bg-base-100 card-bordered card-compact">
                            <div className="card-body">
                                <div className="card-title">
                                    {validatedDriver
                                        ? `${validatedDriver.FirstName} ${validatedDriver.LastName}`
                                        : "Driver"}{" "}
                                    Signature
                                </div>
                                {/* Signature */}
                                <div>
                                    {/* Signature Pad Container */}
                                    {isCheckOut && (
                                        <Controller
                                            name="signatureImageBase64"
                                            control={checkoutForm.control}
                                            render={({
                                                field: { onChange, value },
                                            }) => {
                                                return (
                                                    <SignaturePad
                                                        value={value}
                                                        onChange={onChange}
                                                        error={
                                                            checkoutForm
                                                                .formState
                                                                .errors
                                                                ?.signatureImageBase64
                                                                ?.message
                                                        }
                                                    />
                                                );
                                            }}
                                            rules={{
                                                required:
                                                    "Signature is required",
                                            }}
                                        />
                                    )}
                                    {!isCheckOut && (
                                        <Controller
                                            name="signatureImageBase64"
                                            control={checkInForm.control}
                                            render={({
                                                field: { onChange, value },
                                            }) => {
                                                return (
                                                    <SignaturePad
                                                        value={value}
                                                        onChange={onChange}
                                                        error={
                                                            checkInForm
                                                                .formState
                                                                .errors
                                                                ?.signatureImageBase64
                                                                ?.message
                                                        }
                                                    />
                                                );
                                            }}
                                            rules={{
                                                required:
                                                    "Signature is required",
                                            }}
                                        />
                                    )}

                                    {/* <SignatureCanvas
                    penColor="red"
                    canvasProps={{
                      style: { width: "100%", height: "100px" }, // Syncing the canvas attributes with the CSS dimensions
                    }}
                    ref={(ref) => {
                      sigatureCanvasElementRef.current = ref;
                    }}
                  /> */}

                                    {/* {checkoutForm.formState.errors?.signatureImageBase64?.message && (
                    <p className="text-red-500 mt-1">{checkoutForm.formState.errors?.signatureImageBase64?.message}</p>
                  )} */}
                                </div>
                                <p className="text-xs">
                                    By signing this form, I agree to the
                                    following: I am solely responsible for the
                                    equipment that has been issued to me by the
                                    Company. The equipment belongs to the
                                    Company, information currently on the
                                    equipment is Confidential Company Property
                                    and any information that I add to the
                                    equipment will become Company property. I
                                    have no right to expect privacy while using
                                    Company equipment. The Company reserves the
                                    right, at its sole discretion, to inspect
                                    the equipment and audit it contents. I will
                                    be responsible for any damage to the
                                    equipment/property; excluding normal wear
                                    and tear and system crashes that are not
                                    covered by warranty. I will replace any
                                    items issued to me that are lost or stolen
                                    or damaged beyond repair. Special
                                    circumstances will be reviewed on a case by
                                    case basis. I authorize payroll deduction to
                                    cover the replacement cost of any item
                                    issued to me that is not returned, for
                                    whatever reason, or is not returned in good
                                    working manner. I understand that failure to
                                    return equipment upon demand or upon
                                    separation of employment may be considered
                                    theft from the Company and may lead to
                                    criminal or civil prosecution. I agree
                                    Company can deduct and amounts owed from my
                                    last paycheck. However, if Company is forced
                                    to file suit to recover its equipment or the
                                    cost to replace its equipment, then I will
                                    be responsible for any attorney’s fees
                                    incurred by Company for such action
                                    including the cost of replacement.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        {isCheckOut && (
                            <button
                                className="btn"
                                onClick={() =>
                                    checkoutForm.reset({
                                        terminal: "",
                                        isTabletDamaged: false,
                                        isStylusMissing: false,
                                        issuedBy: "",
                                        tabletSerialNumber: "",
                                        driverCode: "",
                                        unitNumber: "",
                                        signatureImageBase64: "",
                                    })
                                }
                            >
                                Discard
                            </button>
                        )}

                        {!isCheckOut && (
                            <button
                                className="btn"
                                onClick={() =>
                                    checkInForm.reset({
                                        terminal: "",
                                        isTabletDamaged: false,
                                        isStylusMissing: false,
                                        receivedBy: "",
                                        tabletSerialNumber: "",
                                        driverCode: "",
                                        signatureImageBase64: "",
                                    })
                                }
                            >
                                Discard
                            </button>
                        )}

                        {isCheckOut ? (
                            <button
                                className="btn btn-primary"
                                form="checkoutDetails"
                            >
                                {checkoutForm.formState.isSubmitting ? (
                                    <>
                                        <span className="loading loading-spinner loading-xs"></span>
                                        Checking Out...
                                    </>
                                ) : (
                                    "Check-Out"
                                )}
                            </button>
                        ) : (
                            <button
                                className="btn btn-primary"
                                form="checkInDetails"
                            >
                                {checkInForm.formState.isSubmitting ? (
                                    <>
                                        <span className="loading loading-spinner loading-xs"></span>
                                        Checking In...
                                    </>
                                ) : (
                                    "Check-In"
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/* <div className="text-white">Tablet Inventory Management</div>
      <div>
        {terminal.length == 0 ? (
          <div className="text-white" style={{ display: "flex" }}>
            <div className="label">
              <span className="label-text text-white">Terminal: </span>
            </div>
            <select className="text-gray-950" value={terminal} onChange={handleTerminalChange}>
              <option value="">Select a Terminal</option>
              {TabletTerminals.map((terminal) => (
                <option key={terminal} value={terminal}>
                  {terminal}
                </option>
              ))}
            </select>
            <button onClick={handleProceed} className="btn btn-sm btn-primary" style={{ marginRight: "10px" }}>
              Proceed
            </button>
          </div>
        ) : (
          <CheckInOut terminal={terminal} />
        )}
      </div> */}
        </div>
    );
}

const SignaturePad = ({
    onChange,
    error,
    value,
}: {
    onChange: (signatureImageBase64: string) => void;
    error?: string;
    value: string;
}) => {
    const signatureCanvas = useRef(null);

    useEffect(() => {
        if (value === "" && signatureCanvas.current) {
            signatureCanvas.current.clear();
        }
    }, [value]);

    // Pass base64 to parent when stroke ends
    const handleEnd = () => {
        if (onChange && signatureCanvas.current) {
            onChange(signatureCanvas.current.toDataURL());
        }
    };

    return (
        <div
            className={clsx("w-full border relative", {
                "border-red-500": error,
            })}
        >
            <SignatureCanvas
                ref={signatureCanvas}
                penColor="black"
                canvasProps={{
                    style: { width: "100%", height: "100px" }, // Syncing the canvas attributes with the CSS dimensions
                }}
                onEnd={handleEnd}
            />
            <button
                className="absolute px-4 py-2 rounded-tl-lg bg-gray-300 right-0 bottom-0"
                onClick={() => {
                    // signatureCanvas.current.clear();
                    onChange("");
                }}
            >
                <FaEraser />
            </button>
        </div>
        // <SignatureCanvas
        //   ref={sigCanvas}
        //   penColor="black"
        //   canvasProps={{ width: 500, height: 200, className: "signature-canvas" }}
        //   onEnd={handleEnd}
        // />
    );
};

export default TabletInventory;
